/**
 * Layout Payload Builder
 * 
 * Constrói payload para action `exportar-objetos-png-lite` (Fluxo de Layout - Otimizado)
 * 
 * Escala dinâmica baseada na altura máxima do material:
 * - altura > 200cm → escala 0.05 (Sublimação ~450cm)
 * - altura > 150cm → escala 0.12 (DTF Têxtil ~200cm)
 * - altura > 50cm  → escala 0.22 (DTF UV ~100cm, Adesivo ~105cm)
 * - altura ≤ 50cm  → escala 0.50 (DTF UV A3 ~28cm)
 * 
 * Objetivo: PNG de layout com altura final ≤ 24cm
 */

import { sendToTerminal } from '../../../lib/terminalLogger';
import { LAYOUT_EXPORT_CONFIG } from '../constants/layoutConfig';
import { normalizeWindowsPath } from '../../../utils/path';

/**
 * Configuração de escala por faixa de altura do material
 * Objetivo: PNG final com altura ≤ 24cm
 */
const LAYOUT_SCALE_RULES = [
  { maxHeight: Infinity, minHeight: 201, scale: 0.05, description: 'Sublimação (>200cm)' },
  { maxHeight: 200, minHeight: 151, scale: 0.12, description: 'DTF Têxtil/UV grande (151-200cm)' },
  { maxHeight: 150, minHeight: 51, scale: 0.22, description: 'DTF UV/Adesivo (51-150cm)' },
  { maxHeight: 50, minHeight: 0, scale: 0.50, description: 'DTF UV A3/Pequenos (≤50cm)' },
] as const;

/** Escala padrão quando altura do material não está disponível */
const DEFAULT_LAYOUT_SCALE = 0.12;

/**
 * Categorias que NÃO usam contração (Sublimação)
 * Mesma lógica usada na UI (OrderDetailsWindow, HeaderSection)
 */
const HIDDEN_CONTRACTION_CATEG_IDS = [3, 11]; // Sublimação

/**
 * Verifica se o produto é de uma categoria que não usa contração
 * @param categoriaId - ID da categoria do produto (pro_categ_id)
 */
function shouldHideContraction(categoriaId: number | string | null | undefined): boolean {
  if (categoriaId == null) return false;
  return HIDDEN_CONTRACTION_CATEG_IDS.includes(Number(categoriaId));
}

/**
 * Calcula a escala do layout baseada na altura máxima do material
 * 
 * @param alturaMaximaCm - Altura máxima do material em cm (das regras)
 * @returns Escala calculada (0.05 a 0.50)
 * 
 * @example
 * calculateLayoutScale(450) // → 0.05 (Sublimação)
 * calculateLayoutScale(200) // → 0.12 (DTF Têxtil)
 * calculateLayoutScale(100) // → 0.22 (DTF UV)
 * calculateLayoutScale(28)  // → 0.50 (DTF UV A3)
 */
export function calculateLayoutScale(alturaMaximaCm: number | null | undefined): number {
  // Se não tem altura, usar escala padrão
  if (alturaMaximaCm == null || alturaMaximaCm <= 0) {
    sendToTerminal('⚠️ [LAYOUT:SCALE] Altura não disponível, usando escala padrão', {
      alturaMaximaCm,
      defaultScale: DEFAULT_LAYOUT_SCALE,
    });
    return DEFAULT_LAYOUT_SCALE;
  }

  // Encontrar a regra aplicável
  for (const rule of LAYOUT_SCALE_RULES) {
    if (alturaMaximaCm >= rule.minHeight) {
      sendToTerminal('📐 [LAYOUT:SCALE] Escala calculada', {
        alturaMaximaCm,
        scale: rule.scale,
        rule: rule.description,
        estimatedPngHeight: `${(alturaMaximaCm * rule.scale).toFixed(1)}cm`,
      });
      return rule.scale;
    }
  }

  // Fallback (não deve acontecer)
  return DEFAULT_LAYOUT_SCALE;
}

interface LayoutTarget {
  staticId: string;
  guid: string;
  multiplicador?: number;
  contracao?: string | null;
}

interface LayoutPayloadItem {
  staticId: string;
  guid: string;
  multiplicador: number;
  contracao: string | null;
  output_name: string;
}

/**
 * Constrói diretório de saída para layout seguindo estrutura CLIENTES
 * 
 * Estrutura:
 * {baseDir}\CLIENTES\{cli_id}_{cli_nome_fantasia}\{ano}\{mes}\{dd-mm}\{diretorio_nome}\{orcId}\{oitId}\layout\
 */
export function buildLayoutOutputDirectory(savedData: any): string {
  const baseDir = savedData?.regras?.diretorio_base_exportpng || '';
  
  if (!baseDir) {
    throw new Error('diretorio_base_exportpng não configurado nas regras');
  }

  // Dados do cliente - usa nome_fantasia como preferencial, com fallback para nome
  // Sanitização padronizada: normaliza acentos, remove caracteres especiais, substitui espaços por underscore
  const sanitize = (s?: string) =>
    (s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9\s]+/g, " ")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  
  const cliId = savedData?.cliente?.cli_id || savedData?.cliente?.id || '0';
  const cliNome = sanitize(
    savedData?.cliente?.nome_fantasia || savedData?.cliente?.nome || 'Cliente'
  );

  // Data atual
  const now = new Date();
  const ano = now.getFullYear();
  const mesNomes = [
    'JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO',
    'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'
  ];
  const mes = `${String(now.getMonth() + 1).padStart(2, '0')}-${mesNomes[now.getMonth()]}`;
  const dia = String(now.getDate()).padStart(2, '0');
  const mesNum = String(now.getMonth() + 1).padStart(2, '0');
  const diaFormatado = `${dia}-${mesNum}`;

  // Diretório nome (usar diretorio_nome das regras, igual aos outros fluxos)
  const diretorioNome = savedData?.regras?.diretorio_nome || savedData?.regras?.diretorios?.nome || 'INDEFINIDO';

  // IDs do pedido
  const orcId = savedData?.pedido?.orc_id || '0';
  const oitId = savedData?.pedido?.oit_id || '0';

  // Construir caminho
  const path = `${baseDir}\\CLIENTES\\${cliId}_${cliNome}\\${ano}\\${mes}\\${diaFormatado}\\${diretorioNome}\\${orcId}\\${oitId}\\layout`;

  sendToTerminal('📂 [LAYOUT:DIR_BUILT]', {
    baseDir,
    cliente: `${cliId}_${cliNome}`,
    diretorioNome,
    orcId,
    oitId,
    fullPath: path
  });

  return normalizeWindowsPath(path);
}

/**
 * Constrói output_name para cada item do layout
 * 
 * Formato COM contração: [{index:000}]_{material}_[X-{mult}]_{ctr}_[O-{orc}]_[I-{oit}].png
 * Formato SEM contração: [{index:000}]_{material}_[X-{mult}]_[O-{orc}]_[I-{oit}].png
 * 
 * Para produtos de Sublimação (categorias 3, 11), a contração é omitida.
 */
function buildLayoutItemName(
  index: number,
  material: string,
  multiplicador: number,
  contracao: string | null,
  orcId: number | string,
  oitId: number | string
): string {
  const indexStr = String(index).padStart(3, '0');
  
  // Normalizar material (remover espaços e caracteres especiais)
  const materialNorm = material
    .replace(/[<>:"/\\|?*\s]/g, '_')
    .replace(/_+/g, '_')
    .trim();

  // Se contração for null (ex: Sublimação), não incluir no nome
  // Se tiver valor, incluir normalmente
  const ctrPart = contracao ? `_${contracao}` : '';

  // Nome sem extensão (será adicionado automaticamente pelo C#)
  const outputName = `[${indexStr}]_${materialNorm}_[X-${multiplicador}]${ctrPart}_[O-${orcId}]_[I-${oitId}]`;

  return outputName;
}

/**
 * Enriquece targets com output_name
 * 
 * Para produtos de Sublimação (categorias 3, 11), a contração é ignorada
 * pois esse tipo de produto não usa contração.
 */
function enrichLayoutTargets(
  targets: LayoutTarget[],
  savedData: any
): LayoutPayloadItem[] {
  const materialRaw = savedData?.material?.nome || savedData?.produto?.nome || 'Material';
  const orcId = savedData?.pedido?.orc_id || '0';
  const oitId = savedData?.pedido?.oit_id || '0';
  
  // Verificar se é categoria que não usa contração (Sublimação)
  const categoriaId = savedData?.produto?.categoria ?? savedData?.produto?.pro_categ_id ?? null;
  const hideContraction = shouldHideContraction(categoriaId);
  
  if (hideContraction) {
    sendToTerminal('📋 [LAYOUT:CONTRACTION_HIDDEN] Produto não usa contração (Sublimação)', {
      categoriaId,
      hiddenCategories: HIDDEN_CONTRACTION_CATEG_IDS,
    });
  }

  return targets.map((t, idx) => {
    const multiplicador = t.multiplicador ?? 1;
    // Se for sublimação, ignorar contração (usar null)
    const contracao = hideContraction ? null : (t.contracao || null);

    const outputName = buildLayoutItemName(
      idx + 1,
      materialRaw,
      multiplicador,
      contracao,
      orcId,
      oitId
    );

    return {
      staticId: t.staticId,
      guid: t.guid,
      multiplicador,
      contracao,
      output_name: outputName,
    };
  });
}

/**
 * Constrói payload completo para `exportar-objetos-png-lite`
 * 
 * Usa escala dinâmica baseada na altura máxima do material para
 * garantir PNGs de layout com tamanho consistente (~24cm de altura)
 */
export function buildLayoutPngPayload(
  savedData: any,
  targets: LayoutTarget[],
  requestId: string
): any {
  sendToTerminal('🔨 [LAYOUT:BUILD_PAYLOAD_START]', {
    targetsCount: targets.length,
    requestId,
  });

  // 1. Diretório de destino
  const pastaDestino = buildLayoutOutputDirectory(savedData);

  // 2. Configuração de layout (do arquivo de config)
  const { resolucaoDpi, transparencia } = LAYOUT_EXPORT_CONFIG;
  
  // 3. Calcular escala dinâmica baseada na altura máxima do material
  const alturaMaximaCm = savedData?.regras?.altura_maxima_cm ?? null;
  const redimensionarPorEscala = calculateLayoutScale(alturaMaximaCm);

  sendToTerminal('⚙️ [LAYOUT:CONFIG_LOADED]', {
    alturaMaximaCm,
    redimensionarPorEscala,
    resolucaoDpi,
    transparencia,
    source: 'Escala dinâmica baseada em altura_maxima_cm',
  });

  // 4. Enriquecer targets com output_name
  const items = enrichLayoutTargets(targets, savedData);

  sendToTerminal('📋 [LAYOUT:ITEMS_ENRICHED]', {
    itemsCount: items.length,
    preview: items.slice(0, 2).map(i => ({
      staticId: i.staticId,
      outputName: i.output_name,
    })),
  });

  // 5. Nome do layout (opcional, para logs/métricas)
  const orcId = savedData?.pedido?.orc_id || '0';
  const oitId = savedData?.pedido?.oit_id || '0';
  const nomeLayout = `Pedido_${orcId}_Item_${oitId}.cdr`;

  // 6. Construir payload (lite: sem enviarParaWebView, sem smb)
  const payload = {
    action: 'exportar-objetos-png-lite',
    requestId,
    data: {
      pastaDestino,
      redimensionarPorEscala, // ✅ Escala dinâmica baseada no material
      transparencia,
      resolucaoDpi,
      nomeLayout,
      items,
    },
  };

  sendToTerminal('✅ [LAYOUT:PAYLOAD_BUILT]', {
    action: payload.action,
    pastaDestino,
    itemsCount: items.length,
    nomeLayout,
    config: { 
      redimensionarPorEscala,
      alturaMaximaCm,
      resolucaoDpi, 
      transparencia 
    },
  });

  return payload;
}

